import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BED_CLASS } from '@his/shared';
import { Receipt, Tag } from 'lucide-react';
import { get, post, put, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { feeSplittingOnAnywhere } from '@/features/accounts/fee-splitting';
import { humanize, money } from '@/lib/utils';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { RecordState } from '@/components/ui/record-state';
import { Alert, Checkbox, Field, Input, Select } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import type { Rule } from './WardChargeRulesPage';

interface Ward {
  id: string;
  name: string;
}

interface ChargeItem {
  id: string;
  code: string;
  name: string;
  basePriceMinor: number;
}

export function WardChargeRulePage() {
  const { ruleId } = useParams<{ ruleId: string }>();
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get('returnTo'), '/ipd/charge-rules');

  const rules = useQuery({
    queryKey: ['ward-charge-rules'],
    queryFn: () => get<Rule[]>('/ipd/ward-charge-rules'),
    enabled: Boolean(ruleId),
  });

  const rule = ruleId ? (rules.data?.find((row) => row.id === ruleId) ?? null) : null;

  if (ruleId && !rule) {
    return (
      <FormScreen title={tr('ipd', 'edit_ward_charge_rule')} icon={Receipt} backTo={returnTo}>
        <RecordState error={rules.error} missing={!rules.isLoading} label={tr('ipd', 'loading')} />
      </FormScreen>
    );
  }

  return <RuleForm key={rule?.id ?? 'new'} rule={rule} returnTo={returnTo} />;
}

function RuleForm({ rule, returnTo }: { rule: Rule | null; returnTo: string }) {
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const feeSplittingOn = user ? feeSplittingOnAnywhere(user) : true;
  const [initial] = useState(() => ({
    name: rule?.name ?? '',
    wardId: rule?.wardId ?? '',
    bedClass: rule?.bedClass ?? '',
    chargeItemId: rule?.chargeItemId ?? '',
    toDoctor: rule?.performerKind === 'admitting_doctor',
    isActive: rule?.isActive ?? true,
  }));
  const [name, setName] = useState(initial.name);
  const [wardId, setWardId] = useState(initial.wardId);
  const [bedClass, setBedClass] = useState(initial.bedClass);
  const [chargeItemId, setChargeItemId] = useState(initial.chargeItemId);
  const [search, setSearch] = useState('');
  const [toDoctor, setToDoctor] = useState(initial.toDoctor);
  const [isActive, setIsActive] = useState(initial.isActive);
  const [error, setError] = useState<string | null>(null);

  const wards = useQuery({ queryKey: ['wards-list'], queryFn: () => get<Ward[]>('/ipd/wards') });
  const items = useQuery({
    queryKey: ['charge-items', 'ward-rule', search],
    queryFn: () => get<{ data: ChargeItem[] }>(`/charge-items${qs({ q: search || undefined, limit: 40 })}`),
  });

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name,
        wardId: wardId || null,
        bedClass: bedClass || null,
        chargeItemId,
        frequency: 'daily',
        performerKind: toDoctor ? 'admitting_doctor' : 'none',
        isActive,
      };
      return rule ? put(`/ipd/ward-charge-rules/${rule.id}`, body) : post('/ipd/ward-charge-rules', body);
    },
    onSuccess: () => {
      toast.success(rule ? tr('ipd', 'rule_saved') : tr('ipd', 'rule_added'), tr('ipd', 'it_starts_from_today_earlier_days_of_a_stay_are'));
      void queryClient.invalidateQueries({ queryKey: ['ward-charge-rules'] });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const chosen = items.data?.data.find((item) => item.id === chargeItemId) ?? (rule && rule.chargeItemId === chargeItemId ? rule.chargeItem : undefined);
  const ready = name.trim().length >= 2 && Boolean(chargeItemId);
  const dirty =
    !save.isSuccess &&
    (name !== initial.name ||
      wardId !== initial.wardId ||
      bedClass !== initial.bedClass ||
      chargeItemId !== initial.chargeItemId ||
      toDoctor !== initial.toDoctor ||
      isActive !== initial.isActive);

  return (
    <FormScreen
      title={rule ? tr('ipd', 'edit_ward_charge_rule') : tr('ipd', 'add_a_ward_charge_rule')}
      description={tr('ipd', 'charges_the_system_adds_to_every_admitted')}
      eyebrow={tr('ipd', 'ward_charges')}
      icon={Receipt}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready) return;
        setError(null);
        save.mutate();
      }}
    >
      <FormSection title={tr('ipd', 'rule')} icon={Tag}>
        <Field label={tr('ipd', 'name')} required hint={tr('ipd', 'shown_on_the_bill_followed_by_the_date')} className="sm:col-span-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={tr('ipd', 'daily_consultant_visit')} />
        </Field>
        <Field label={tr('ipd', 'ward')} hint={tr('ipd', 'leave_blank_for_every_ward')}>
          <Select value={wardId} onChange={(e) => setWardId(e.target.value)}>
            <option value="">{tr('ipd', 'every_ward')}</option>
            {wards.data?.map((ward) => (
              <option key={ward.id} value={ward.id}>
                {ward.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={tr('ipd', 'bed_class')} hint={tr('ipd', 'leave_blank_for_every_class')}>
          <Select value={bedClass} onChange={(e) => setBedClass(e.target.value)}>
            <option value="">{tr('ipd', 'every_class')}</option>
            {BED_CLASS.map((value) => (
              <option key={value} value={value}>
                {humanize(value, 'bed_class')}
              </option>
            ))}
          </Select>
        </Field>
      </FormSection>

      <FormSection title={tr('ipd', 'charge_item')} icon={Receipt} columns={1}>
        <Field label={tr('ipd', 'charge_item')} required hint={chosen ? `${chosen.code} · ${chosen.name} · ${money(chosen.basePriceMinor)}` : tr('ipd', 'search_the_charge_master')}>
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={tr('ipd', 'search_by_name_or_code')} />
          <div className="mt-2 max-h-40 overflow-y-auto rounded-md border">
            {items.data?.data.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setChargeItemId(item.id)}
                className={`flex w-full justify-between px-3 py-1.5 text-start text-sm hover:bg-accent focus-ring ${item.id === chargeItemId ? 'bg-accent' : ''}`}
              >
                <span>
                  <span className="font-mono text-xs text-primary">{item.code}</span> {item.name}
                </span>
                <span className="tabular text-muted-foreground">{money(item.basePriceMinor)}</span>
              </button>
            ))}
          </div>
        </Field>

        <Checkbox
          checked={toDoctor}
          onCheckedChange={setToDoctor}
          label={feeSplittingOn ? tr('ipd', 'credit_this_charge_to_the_admitting_doctor') : tr('ipd', 'credit_this_charge_to_the_admitting_doctor_2')}
        />

        <Checkbox checked={isActive} onCheckedChange={setIsActive} label={tr('ipd', 'active_2')} />
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions submitLabel={tr('ipd', 'save_rule')} loading={save.isPending} incomplete={!ready} cancelTo={returnTo} />
    </FormScreen>
  );
}
